import React, { useMemo, useState } from 'react';
import { Bell, Search, Send, Sparkles, UserPlus } from 'lucide-react';
import { Avatar, AvatarFallback } from './Avatar';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './Dialog';
import { EmiratiBadge } from './EmiratiBadge';
import { Input } from './Input';
import { Label } from './Label';
import { Textarea } from './Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { useEvents } from '../contexts/EventsContext';
import { experts } from '../data/experts';
import { expertRoles } from '../data/bakEvents';
import type { BakEvent, ExpertRole } from '../types/event';
import { averageRating, formatAED, getInitials } from '../utils/expert';
import { getTier } from '../utils/tier';
import { TierBadge } from './TierBadge';
type InviteExpertsDialogProps = {
  event: BakEvent;
};
export function InviteExpertsDialog({
  event
}: InviteExpertsDialogProps) {
  const {
    inviteExperts
  } = useEvents();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [role, setRole] = useState<ExpertRole>('Panelist');
  const [message, setMessage] = useState('');
  const invitedIds = new Set(event.invitations.map(i => i.expertId));
  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return experts.filter(e => !invitedIds.has(e.id)).filter(e => !q || e.name.toLowerCase().includes(q) || e.expertise.some(x => x.name.toLowerCase().includes(q))).map(e => ({
      expert: e,
      match: e.expertise.find(x => x.name === event.category)?.confidence ?? 0
    })).sort((a, b) => b.match - a.match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, event.invitations, event.category]);
  const toggle = (id: string) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const reset = () => {
    setQuery('');
    setSelected([]);
    setRole('Panelist');
    setMessage('');
  };
  const handleSend = () => {
    if (selected.length === 0) return;
    inviteExperts(event.id, {
      expertIds: selected,
      role,
      message
    });
    setOpen(false);
    reset();
  };
  return <>
      <Button onClick={() => setOpen(true)} data-id="element-387" data-mp="26hoa7hqcxczw">
        <UserPlus data-id="element-388" data-mp="26gd4vdh9jx6o" /> Invite experts
      </Button>
      <Dialog open={open} onOpenChange={o => {
      setOpen(o);
      if (!o) reset();
    }} data-id="element-389" data-mp="22gmj5o6lg790">
        <DialogContent className="sm:max-w-2xl" data-id="element-390" data-mp="22ip59x4mlg8g">
          <DialogHeader data-id="element-391" data-mp="2gf2mr695thmx">
            <DialogTitle data-id="element-392" data-mp="2gfcg93tey6xd">Invite experts</DialogTitle>
            <DialogDescription data-id="element-393" data-mp="2bgizn2itxzsz">Selected experts are notified right away and can accept or decline from the BAK expert app.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4" data-id="element-394" data-mp="m3xokhgudxez">
            <div className="relative" data-id="element-395" data-mp="h41mwovmai2l">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" data-id="element-396" data-mp="h54d0ul48ghg" />
              <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search experts or fields…" className="pl-9" aria-label="Search experts" data-id="element-397" data-mp="2xpptb0m5a41y" />
            </div>

            <ul className="max-h-72 divide-y overflow-y-auto rounded-lg border" aria-label="Available experts" data-id="element-398" data-mp="2nzdd1kld4x1m">
              {candidates.length === 0 && <li className="p-6 text-center text-sm text-muted-foreground" data-id="element-399" data-mp="29ykfss3ofnut">
                  {invitedIds.size === experts.length ? 'Every registered expert has already been invited.' : 'No experts match your search.'}
                </li>}
              {candidates.map(({
              expert,
              match
            }, i) => {
              const checked = selected.includes(expert.id);
              const id = `invite-${expert.id}`;
              return <li key={expert.id} data-id="element-400" data-mp="39nx148xb57c" data-mpi={i}>
                    <label htmlFor={id} className={`flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors hover:bg-muted/50 ${checked ? 'bg-muted/60' : ''}`} data-id="element-401" data-mp="38mlkynzwgp3">
                      <Checkbox id={id} checked={checked} onCheckedChange={() => toggle(expert.id)} data-id="element-402" data-mp="1vc0bbfk1iwft" />
                      <Avatar size="sm" data-id="element-403" data-mp="1qakrfqtlmvz0"><AvatarFallback data-id="element-404" data-mp="1qa194901e5kg">{getInitials(expert.name)}</AvatarFallback></Avatar>
                      <div className="min-w-0 flex-1" data-id="element-405" data-mp="1lc9irjf1irid">
                        <div className="flex flex-wrap items-center gap-1.5" data-id="element-406" data-mp="1gdg6fubhp6js">
                          <span className="truncate text-sm font-medium" data-id="element-407" data-mp="1gfj0cpfrjjts">{expert.name}</span>
                          {expert.isEmirati && <EmiratiBadge compact data-id="element-408" data-mp="17l4q51a3azhj" />}
                          <TierBadge tier={getTier(expert)} data-id="element-409" data-mp="17lw8vbil92qe" />
                          {i === 0 && match > 0 && !query && <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700" data-id="element-410" data-mp="12kgntjgeuw80">
                              <Sparkles className="size-3" aria-hidden="true" data-id="element-411" data-mp="spf0mt5p4482" /> Best match
                            </span>}
                        </div>
                        <p className="truncate text-xs text-muted-foreground" data-id="element-412" data-mp="nigw1wtpheff">
                          {expert.title} · ★ {averageRating(expert).toFixed(1)} · {expert.availability}
                        </p>
                      </div>
                      <div className="hidden text-right sm:block" data-id="element-413" data-mp="xgoaa7wm1idq">
                        <p className="text-xs font-medium tabular-nums" data-id="element-414" data-mp="3w04pb2p5nbph">{formatAED(expert.dailyRate)}</p>
                        <p className="text-[11px] text-muted-foreground" data-id="element-415" data-mp="4ttq7c3pgl0s">{match > 0 ? `${match}% field match` : 'Other field'}</p>
                      </div>
                    </label>
                  </li>;
            })}
            </ul>

            <div className="grid gap-4 sm:grid-cols-[200px_1fr]" data-id="element-416" data-mp="3d8llitnb3tme">
              <div className="grid gap-1.5" data-id="element-417" data-mp="3d8bwb8a35qhr">
                <Label htmlFor="invite-role" data-id="element-418" data-mp="3i759ezlgnfhe">Role</Label>
                <Select value={role} onValueChange={v => setRole(v as ExpertRole)} data-id="element-419" data-mp="3i5u3ou8gfxnn">
                  <SelectTrigger id="invite-role" className="w-full" data-id="element-420" data-mp="3a5efqpzry95w"><SelectValue data-id="element-421" data-mp="3585ycrmga95p" /></SelectTrigger>
                  <SelectContent data-id="element-422" data-mp="357ctxxayrvqq">
                    {expertRoles.map(r => <SelectItem key={r} value={r} data-id="element-423" data-mp="3jxbv88xtfnaf">{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5" data-id="element-424" data-mp="3eyhah1gafono">
                <Label htmlFor="invite-msg" data-id="element-425" data-mp="2r7lbnkdezt6x">Personal message (optional)</Label>
                <Textarea id="invite-msg" rows={2} value={message} onChange={e => setMessage(e.target.value)} placeholder={`We'd love you to join ${event.name}…`} data-id="element-426" data-mp="2m91o5fj6a9cg" />
              </div>
            </div>

            <p className="flex items-center gap-2 rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground" data-id="element-427" data-mp="2w77qtzcszjid">
              <Bell className="size-3.5 shrink-0" aria-hidden="true" data-id="element-428" data-mp="gfk57038pdev" />
              Notification is sent automatically via email, SMS and the BAK expert app with event details and proposed fee.
            </p>
          </div>

          <DialogFooter data-id="element-429" data-mp="izgl3pqdd6sd">
            <Button variant="outline" onClick={() => setOpen(false)} data-id="element-430" data-mp="iz6qtpyakdgv">Cancel</Button>
            <Button onClick={handleSend} disabled={selected.length === 0} data-id="element-431" data-mp="o15ul0xy1cec">
              <Send data-id="element-432" data-mp="a0l89ustkyg3" /> Send {selected.length > 0 ? `${selected.length} ` : ''}invitation{selected.length === 1 ? '' : 's'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>;
}